import { Injectable, OnDestroy, inject } from '@angular/core';
import { OKTA_CONFIG } from '@okta/okta-angular';
import { AccessToken, OktaAuth } from '@okta/okta-auth-js';

type TabTokenMessage =
  | { type: 'token-probe'; requestId: string; tokenId: string }
  | { type: 'token-in-use'; requestId: string };

const TOKEN_CHANNEL_NAME = 'maap-tab-access-token';
const TOKEN_PROBE_WAIT_MS = 250;
const MAX_TOKEN_RENEWALS = 3;

@Injectable({
  providedIn: 'root'
})
export class TabTokenService implements OnDestroy {
  private readonly oktaAuth: OktaAuth = inject(OKTA_CONFIG).oktaAuth;
  private readonly channel = typeof BroadcastChannel === 'undefined'
    ? undefined
    : new BroadcastChannel(TOKEN_CHANNEL_NAME);
  private readonly pendingProbes = new Map<string, (inUse: boolean) => void>();
  private currentTokenId?: string;
  private initializationPromise?: Promise<void>;

  constructor() {
    this.channel?.addEventListener('message', this.handleMessage);
    this.oktaAuth.tokenManager.on('renewed', this.handleTokenRenewed);
  }

  ensureUniqueAccessToken(): Promise<void> {
    if (!this.oktaAuth.getAccessToken()) {
      return Promise.resolve();
    }

    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeCurrentToken()
        .catch(error => {
          this.initializationPromise = undefined;
          throw error;
        });
    }

    return this.initializationPromise;
  }

  releaseCurrentToken(): void {
    this.currentTokenId = undefined;
    this.initializationPromise = undefined;
  }

  ngOnDestroy(): void {
    this.channel?.removeEventListener('message', this.handleMessage);
    this.channel?.close();
    this.oktaAuth.tokenManager.off('renewed', this.handleTokenRenewed);
  }

  private async initializeCurrentToken(): Promise<void> {
    let accessToken = await this.getAccessToken();
    if (!accessToken) {
      return;
    }

    if (!this.channel) {
      accessToken = await this.renewAccessToken();
      this.currentTokenId = await this.getTokenId(accessToken);
      return;
    }

    for (let attempt = 0; attempt <= MAX_TOKEN_RENEWALS; attempt += 1) {
      this.currentTokenId = await this.getTokenId(accessToken);
      if (!(await this.isTokenAlreadyInUse(this.currentTokenId))) {
        return;
      }

      if (attempt === MAX_TOKEN_RENEWALS) {
        break;
      }
      accessToken = await this.renewAccessToken();
    }

    throw new Error('Unable to establish a unique access token for this tab');
  }

  private async getAccessToken(): Promise<AccessToken | undefined> {
    return await this.oktaAuth.tokenManager.get('accessToken') as AccessToken | undefined;
  }

  private async renewAccessToken(): Promise<AccessToken> {
    const renewedToken = await this.oktaAuth.tokenManager.renew('accessToken') as AccessToken | undefined;
    if (!renewedToken?.accessToken) {
      throw new Error('Okta did not return a renewed access token');
    }
    return renewedToken;
  }

  private async getTokenId(token: AccessToken): Promise<string> {
    const jti = token.claims?.['jti'];
    if (typeof jti === 'string' && jti.length > 0) {
      return jti;
    }

    const tokenBytes = new TextEncoder().encode(token.accessToken);
    const digest = await crypto.subtle.digest('SHA-256', tokenBytes);
    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private isTokenAlreadyInUse(tokenId: string): Promise<boolean> {
    if (!this.channel) {
      return Promise.resolve(false);
    }

    const requestId = `${Date.now()}-${Math.random()}`;
    return new Promise(resolve => {
      const timeoutId = window.setTimeout(() => {
        this.pendingProbes.delete(requestId);
        resolve(false);
      }, TOKEN_PROBE_WAIT_MS);

      this.pendingProbes.set(requestId, inUse => {
        window.clearTimeout(timeoutId);
        this.pendingProbes.delete(requestId);
        resolve(inUse);
      });
      this.channel?.postMessage({ type: 'token-probe', requestId, tokenId } satisfies TabTokenMessage);
    });
  }

  private readonly handleMessage = (event: MessageEvent<TabTokenMessage>): void => {
    const message = event.data;

    if (message.type === 'token-probe' && message.tokenId === this.currentTokenId) {
      this.channel?.postMessage({ type: 'token-in-use', requestId: message.requestId } satisfies TabTokenMessage);
      return;
    }

    if (message.type === 'token-in-use') {
      this.pendingProbes.get(message.requestId)?.(true);
    }
  };

  private readonly handleTokenRenewed = (key: string, token: unknown): void => {
    if (key !== 'accessToken') {
      return;
    }

    void this.getTokenId(token as AccessToken).then(tokenId => {
      this.currentTokenId = tokenId;
    });
  };
}
